
import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from "./ResetPassword.module.scss";
import Loader from "../UiComponents/loader/Loader";
import { FaChevronRight } from "react-icons/fa";

import { handleValidation, strongRegex } from "../../utils/formValidation";
import logoHS from "../../pics/logo-hearingspace.png";
import ContainerForm from "../UiComponents/forms/ContainerForm";
import InputPassword from "../UiComponents/forms/InputPassword";
import BtnForms from "../UiComponents/forms/BtnForms";
import RulesPassword from "../UiComponents/forms/RulesPassword";

import { toast } from "react-toastify";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [errorValidation, setErrorValidation] = useState(null);

  // ✅ CHANGEMENT: écran de validation
  const [validatingLink, setValidatingLink] = useState(true);

  const [loading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [displayForms, setDisplayForms] = useState(false);

  const [resetToken, setResetToken] = useState(null);
  const [resetId, setResetId] = useState(null);

  const getTokenAndIdFromHash = () => {
    // 🔐 Lecture du token depuis le fragment (#) de l’URL (non envoyé au serveur)
      // 🔐 Lecture sécurisée du token depuis le fragment (#) de l’URL.
// Le fragment n’est JAMAIS envoyé au serveur HTTP (contrairement aux query params),
// ce qui évite toute fuite du token via les logs, le referer ou les proxies.
// On retire le "#" pour pouvoir parser facilement les paramètres.
    const rawHash = (window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(rawHash);

    return {
      token: params.get("token"),
      id: params.get("id"),
      hasSensitiveInUrl: Boolean(params.get("token") || params.get("id")),
    };
  };

  useEffect(() => {
    const run = async () => {
      setValidatingLink(true);

      const { token, id, hasSensitiveInUrl } = getTokenAndIdFromHash();

      if (!token || !id) {
        toast.error("Lien de réinitialisation invalide. Merci de refaire la procédure.");
        setDisplayForms(false);
        setValidatingLink(false);
        return;
      }

      // ✅ Stocke token/id avant nettoyage
      setResetToken(token);
      setResetId(id);

      // 🔐 Nettoie immédiatement l’URL (supprime le token visible sans recharger)
      if (hasSensitiveInUrl) {
           // 🔐 Sécurité : on nettoie immédiatement l’URL pour supprimer le token visible.
// Cette opération modifie l’URL affichée dans le navigateur SANS recharger la page.
// Cela empêche les fuites de token via l’historique, les captures d’écran,
// le copier-coller ou les headers HTTP (Referer).
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      try {
        // ✅ Vérification UX (pas une sécurité): le backend confirme que le token est valide/non expiré
        const res = await axios.post(`${process.env.REACT_APP_API_URL}api/verifyToken`, {
          data: { token, id },
        });

        if (res.data?.ok) {
          setDisplayForms(true);
        } else {
          toast.error("Lien de réinitialisation invalide ou expiré. Merci de refaire la procédure.");
          setDisplayForms(false);
        }
      } catch (e) {
        toast.error("Lien de réinitialisation invalide ou expiré. Merci de refaire la procédure.");
        setDisplayForms(false);
      } finally {
        setValidatingLink(false);
      }
    };

    run();
  }, []);

  const submitChangePassword = async (e) => {
    e.preventDefault();

    if (!resetToken || !resetId) {
      toast.error("Lien de réinitialisation invalide. Merci de refaire la procédure.");
      return;
    }

    if (password !== password2) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (!strongRegex.test(password)) {
      toast.error("Le mot de passe n'est pas assez fort");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.put(`${process.env.REACT_APP_API_URL}api/resetPassword`, {
        data: { token: resetToken, id: resetId, password, password2 },
      });

      setLoading(false);

      if (res.data?.error) {
        toast.error(res.data.message || "Lien de réinitialisation invalide ou expiré.");
        setDisplayForms(false);
        return;
      }

      toast.success(res.data.message || "Mot de passe modifié avec succès");
      setShowSuccessMessage(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      console.error(err);
      setLoading(false);
      toast.error("Une erreur est survenue. Merci de refaire la procédure.");
      setDisplayForms(false);
    }
  };

  return (
    <>
      {(loading || validatingLink) && <Loader />}

      {showSuccessMessage ? (
        <div className={styles.successMessage}>
          <img src={logoHS} alt="Hearing Space Logo" />
          <h2>Votre mot de passe a été changé avec succès !</h2>
          <p>
            Vous pouvez maintenant retourner sur Hearing Space. Si vous avez besoin
            d'assistance, n'hésitez pas à contacter le support.
          </p>
        </div>
      ) : displayForms && !loading && !validatingLink ? (
        <div className="fixed-container-modals">
          <img className={styles.logo} src={logoHS} alt="Hearing Space Logo" />

          <ContainerForm
            onSubmit={submitChangePassword}
            modal={true}
            children={
              <>
                <InputPassword
                  value={password}
                  onChange={(e) => setPassword(e.target.value.trim())}
                  placeholder="Nouveau mot de passe"
                  onKeyUp={(e) => setErrorValidation(handleValidation(e))}
                />

                <InputPassword
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value.trim())}
                  placeholder="Nouveau mot de passe"
                />

                <BtnForms text="Réinitialiser mon mot de passe" svg={<FaChevronRight />} />
                <RulesPassword />
              </>
            }
          />
        </div>
      ) : !validatingLink ? (
        <div className={styles.error_token}>
          <img src={logoHS} alt="Hearing Space Logo" />
          <p>
            Une erreur est survenue lors de la réinitialisation de votre mot de passe.
            Merci de refaire la procédure.
          </p>
          <a href={process.env.REACT_APP_HOME}>Accueil DashBoard Hearing Space</a>
        </div>
      ) : null}
    </>
  );
};

export default ResetPassword;
